import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ConsoleRecordingSummary } from "@victor/api-client/console";
import { api } from "../../lib/api";
import { Card } from "../../ui/Card";
import { PageSpinner } from "../../ui/Spinner";

/**
 * Sessions · replays PostHog (site vitrine + plateforme) sans quitter la
 * console. La liste et les jetons d'embed passent par l'API Victor
 * (super-admin), la clé PostHog ne touche jamais le navigateur.
 */

type SurfaceTab = "site" | "app";

const TABS: Array<{ id: SurfaceTab; label: string }> = [
  { id: "site", label: "Site vitrine" },
  { id: "app", label: "Plateforme" },
];

function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m} min ${sec.toString().padStart(2, "0")} s` : `${sec} s`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SessionsPage() {
  const [tab, setTab] = useState<SurfaceTab>("app");
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recordingsQ = useQuery({
    queryKey: ["console-recordings"],
    queryFn: () => api.analytics.listRecordings(100),
    refetchInterval: 60_000,
  });

  const rows = (recordingsQ.data ?? []).filter((r) => r.surface === tab);

  async function openPlayer(r: ConsoleRecordingSummary) {
    setError(null);
    setLoadingId(r.id);
    // Ouvre l'onglet AVANT l'await (sinon le bloqueur de pop-up coupe le
    // window.open différé). Le lecteur PostHog s'affiche en plein écran :
    // ni le frame-ancestors ni les bloqueurs de contenu ne s'appliquent à
    // une navigation top-level (contrairement à une iframe).
    const win = window.open("", "_blank");
    try {
      const { url } = await api.analytics.recordingEmbed(r.id);
      if (win) win.location.href = url;
      else window.open(url, "_blank"); // pop-up bloquée : 2e tentative
    } catch {
      win?.close();
      setError("Impossible de charger ce replay. Réessayez dans un instant.");
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-800">
            Sessions
          </h1>
          <p className="mt-1 text-sm text-navy-600">
            Replays des visites (site vitrine) et de l'utilisation de la
            plateforme. Les saisies sont masquées. Actualisation auto chaque
            minute.
          </p>
        </div>
        <a
          href="https://eu.posthog.com/project/193287/replay/home"
          target="_blank"
          rel="noreferrer"
          className="text-sm font-semibold text-accent-600 hover:underline"
        >
          Ouvrir dans PostHog →
        </a>
      </header>

      {/* Onglets surface */}
      <div className="flex gap-1 rounded-full bg-navy-50 p-1 ring-1 ring-navy-100 w-fit">
        {TABS.map((t) => {
          const count = (recordingsQ.data ?? []).filter(
            (r) => r.surface === t.id,
          ).length;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                tab === t.id
                  ? "bg-white text-navy-800 shadow-sm ring-1 ring-navy-100"
                  : "text-navy-600 hover:text-navy-800"
              }`}
            >
              {t.label}
              <span className="rounded-full bg-navy-100 px-1.5 py-0.5 text-[11px] font-black text-navy-600">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      ) : null}

      {recordingsQ.isLoading ? (
        <PageSpinner />
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-sm text-navy-600">
            Aucun replay {tab === "site" ? "du site vitrine" : "de la plateforme"}{" "}
            pour le moment. Les sessions apparaissent ici quelques minutes
            après la visite.
          </p>
        </Card>
      ) : (
        <Card padded={false}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-100 text-left text-xs font-bold uppercase tracking-wider text-navy-500">
                <th className="px-4 py-3">Début</th>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Page d'entrée</th>
                <th className="px-4 py-3 text-right">Durée</th>
                <th className="px-4 py-3 text-right">Clics</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-navy-50 last:border-0 hover:bg-navy-50/60"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-navy-700">
                    {fmtDate(r.startTime)}
                    {r.ongoing ? (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                        En cours
                      </span>
                    ) : null}
                  </td>
                  <td className="max-w-[220px] truncate px-4 py-3 font-semibold text-navy-800">
                    {r.personLabel ?? "Visiteur anonyme"}
                  </td>
                  <td className="max-w-[260px] truncate px-4 py-3 text-navy-600">
                    {r.startUrl ? new URL(r.startUrl).pathname : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-navy-700">
                    {fmtDuration(r.durationSeconds)}
                  </td>
                  <td className="px-4 py-3 text-right text-navy-700">
                    {r.clickCount}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openPlayer(r)}
                      disabled={loadingId === r.id}
                      className="rounded-lg bg-accent-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
                    >
                      {loadingId === r.id ? "Ouverture…" : "▶ Regarder ↗"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
